// GSM-7 / UCS-2 SMS segment calculator.
//
// Plain ES module, no dependencies. Mirrors verstaani/utils/sms_segments.py — both
// implementations are verified against the shared fixture smsSegmentVectors.json and must
// never drift apart.

// GSM 03.38 basic character set: each character costs 1 septet. Includes LF and CR.
const GSM7_BASIC_CHARSET = new Set(
  '@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
    '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑܧ¿abcdefghijklmnopqrstuvwxyzäöñüà' +
    '\n\r'
);

// GSM 03.38 extension table: each character costs 2 septets (ESC + char). Includes form feed.
const GSM7_EXTENSION_CHARSET = new Set('^{}\\[]~|€\f');

const GSM7_SINGLE_SEGMENT_LIMIT = 160;
const GSM7_MULTI_SEGMENT_LIMIT = 153;
const UCS2_SINGLE_SEGMENT_LIMIT = 70;
const UCS2_MULTI_SEGMENT_LIMIT = 67;

/**
 * Calculate SMS encoding, billing units and segment count for a message text.
 *
 * A message consisting only of GSM 03.38 characters is sent as GSM-7 (basic chars cost
 * 1 septet, extension chars 2). Any other character forces the whole message to UCS-2,
 * where the unit is a UTF-16 code unit. An empty message still bills one segment.
 *
 * Iterates UTF-16 code units (not code points) on purpose: a lone surrogate half is never
 * in the GSM sets, so any astral character (e.g. emoji) correctly forces UCS-2.
 *
 * @param {string|null|undefined} text
 * @returns {{ encoding: 'gsm7'|'ucs2', unitCount: number, segmentCount: number, remainingInSegment: number }}
 */
export function calculateSmsSegments(text) {
  const value = text == null ? '' : String(text);

  let septets = 0;
  let isGsm7 = true;
  for (let i = 0; i < value.length; i++) {
    const char = value.charAt(i);
    if (GSM7_BASIC_CHARSET.has(char)) {
      septets += 1;
    } else if (GSM7_EXTENSION_CHARSET.has(char)) {
      septets += 2;
    } else {
      isGsm7 = false;
      break;
    }
  }

  let encoding;
  let unitCount;
  let singleLimit;
  let multiLimit;
  if (isGsm7) {
    encoding = 'gsm7';
    unitCount = septets;
    singleLimit = GSM7_SINGLE_SEGMENT_LIMIT;
    multiLimit = GSM7_MULTI_SEGMENT_LIMIT;
  } else {
    encoding = 'ucs2';
    unitCount = value.length; // UTF-16 code units
    singleLimit = UCS2_SINGLE_SEGMENT_LIMIT;
    multiLimit = UCS2_MULTI_SEGMENT_LIMIT;
  }

  let segmentCount;
  let capacity;
  if (unitCount <= singleLimit) {
    segmentCount = 1;
    capacity = singleLimit;
  } else {
    segmentCount = Math.ceil(unitCount / multiLimit);
    capacity = segmentCount * multiLimit;
  }

  return {
    encoding,
    unitCount,
    segmentCount,
    remainingInSegment: capacity - unitCount,
  };
}
